import { MenuItemType, MenuType } from "../../../../types/baseTypes";
import styles from "./SubMenu.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import MenuItem from "../MenuItem/MenuItem";
import { MenuParentType } from "../Menu";
import { isLink, isMenuOrChildMenuSelected } from "../utils";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import React, { useEffect, useRef, useState } from "react";
import { useKeyboardNavigation } from "../keyboard-navigation";
import { Post } from "@aleksundshantu/gp-types/client";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { CustomLink } from "@/components/CustomLink";
import { CulinaryCard } from "../MegaMenu/CulinaryCard";
import { useAppContext } from "@/context/AppContext";
import type { MegaMenuHighlightType } from "../MegaMenu/MegaMenu";
import {
  getMegaMenuColumns,
  hasVisibleMegaMenuTitle,
} from "../MegaMenu/MegaMenu";
import type { OptionsNavigationItemType } from "@/types/OptionType";
import { customSanitize } from "@/utils/customSanitize";

interface SubMenuItemProps {
  item: MenuItemType;
  pageData?: Post;
  currentSubmenu: MenuParentType;
  parentSubMenu: MenuParentType;
  subMenu: MenuParentType;
  parentMenuItem: MenuItemType;
  setCurrentSubmenu: (value: MenuParentType) => void;
  controlParentMenu: (action: "close" | "prev" | "next") => void;
  className?: string;
  culinary?: boolean;
  megaMenuOption?: OptionsNavigationItemType;
  megaMenuHighlights?: MegaMenuHighlightType[];
  topMenu?: MenuType | null;
}

const hasClass = (item: MenuItemType, className: string) =>
  item.classes?.includes(className);

const SanitizedText = ({ text }: { text: string }) => (
  <span dangerouslySetInnerHTML={{ __html: customSanitize(text) }} />
);

const SubMenu = ({
  item,
  pageData,
  currentSubmenu,
  setCurrentSubmenu,
  parentSubMenu,
  subMenu,
  parentMenuItem,
  controlParentMenu,
  className,
  culinary = false,
  megaMenuOption,
  megaMenuHighlights = [],
  topMenu: _topMenu = null,
}: SubMenuItemProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const { setOpenMenu } = useAppContext();
  const { items, keyDown, controlMenu, firstItemFocused } =
    useKeyboardNavigation({
      menu: item,
      setCurrentSubmenu,
      type: "submenu",
      controlParentMenu,
      subMenu,
    });
  const ref = useRef<HTMLUListElement | null>(null);
  const [outOfViewport, setOutOfViewport] = useState(false);
  const columns = getMegaMenuColumns(item.child_items || []);
  const hasGroupedMegaMenu = !!megaMenuOption && !culinary;
  const fallbackLinks = (item.child_items || []).filter(
    (child) =>
      !hasClass(child, "group-header") && !hasClass(child, "group-bottom"),
  );
  const visibleHighlights = megaMenuHighlights.filter(
    (highlight) => highlight.href,
  );
  const closeMenu = () => {
    setOpenMenu(false);
    setCurrentSubmenu(undefined);
  };
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // true when element not fully inside
        setOutOfViewport(entry.intersectionRatio < 1);
      },
      {
        threshold: 0,
      },
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <FocusTrap
      innerRef={ref}
      dep={currentSubmenu}
      disabled={currentSubmenu?.ID !== item.ID}
    >
      <ul
        className={`${styles["navbar-submenu"]} ${className || ""} ${outOfViewport ? styles["out-of-viewport"] : ""} navbar-submenu ${currentSubmenu?.ID !== item.ID ? "trap enabled" : "trap disabled"} ${isMenuOrChildMenuSelected(item, currentSubmenu) ? styles["is-open"] : ""}`}
        role="menu"
        onKeyDown={keyDown}
      >
        <li
          className={`${styles["navbar-submenu-title"]} navbar-link is-hidden-after-navbar-breakpoint`}
        >
          {parentMenuItem.title}
        </li>
        {culinary ? (
          <>
            <li className={styles["culinary-grid"]} role="none">
              {(item.child_items || []).map((child) => (
                <CulinaryCard
                  key={child.ID ?? child.url}
                  item={child}
                  onNavigate={closeMenu}
                />
              ))}
            </li>
            {megaMenuOption && isLink(megaMenuOption.link_menu_footer) && (
              <li className={styles["culinary-footer"]} role="none">
                <CustomLink
                  className={styles["culinary-footer-link"]}
                  href={megaMenuOption.link_menu_footer.url}
                  target={megaMenuOption.link_menu_footer.target}
                  onClick={closeMenu}
                  disablePrefetch
                >
                  <span className={styles["culinary-footer-icon"]}>
                    <CustomSVG
                      width={24}
                      height={24}
                      src={getIcon("arrowRight")}
                      wrapper="span"
                      aria-hidden="true"
                    />
                  </span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: customSanitize(
                        megaMenuOption.link_menu_footer.title,
                      ),
                    }}
                  />
                </CustomLink>
                {megaMenuOption.content && (
                  <div
                    className={styles["culinary-footer-text"]}
                    dangerouslySetInnerHTML={{
                      __html: customSanitize(megaMenuOption.content),
                    }}
                  />
                )}
              </li>
            )}
          </>
        ) : hasGroupedMegaMenu ? (
          <>
            <li className={styles["mobile-mega-sections"]} role="none">
              {(columns.length > 0
                ? columns
                : [
                    {
                      header: parentMenuItem,
                      links: fallbackLinks,
                    },
                  ]
              ).map((column, columnIndex) => {
                const visibleLinks = column.links.filter(
                  hasVisibleMegaMenuTitle,
                );

                return (
                  <div
                    role="group"
                    className={styles["mobile-mega-section"]}
                    key={
                      column.header.ID ??
                      `${column.header.title}-${columnIndex}`
                    }
                    aria-labelledby={`mobile-mega-section-${item.ID}-${columnIndex}`}
                  >
                    <span
                      className={styles["mobile-mega-title"]}
                      id={`mobile-mega-section-${item.ID}-${columnIndex}`}
                    >
                      <SanitizedText text={column.header.title} />
                    </span>
                    {(visibleLinks.length > 0 || column.bottomLink) && (
                      <ul className={styles["mobile-mega-link-grid"]}>
                        {visibleLinks.map((link) => (
                          <li key={link.ID ?? link.url}>
                            <CustomLink
                              className={styles["mobile-mega-link"]}
                              href={link.url}
                              target={link.target}
                              onClick={closeMenu}
                              disablePrefetch
                            >
                              <SanitizedText text={link.title} />
                            </CustomLink>
                          </li>
                        ))}
                        {column.bottomLink && (
                          <li
                            key={column.bottomLink.ID ?? column.bottomLink.url}
                          >
                            <CustomLink
                              className={styles["mobile-mega-more-link"]}
                              href={column.bottomLink.url}
                              target={column.bottomLink.target}
                              onClick={closeMenu}
                              disablePrefetch
                            >
                              <CustomSVG
                                width={26}
                                height={18}
                                src={getIcon("bare_arrow_right")}
                                wrapper="span"
                                aria-hidden="true"
                              />
                              <SanitizedText text={column.bottomLink.title} />
                            </CustomLink>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </li>

            {(visibleHighlights.length > 0 ||
              isLink(megaMenuOption.link_column_end)) && (
              <li className={styles["mobile-mega-section"]} role="none">
                <span className={styles["mobile-mega-title"]}>
                  {megaMenuOption.submenu_title ||
                    getLabel("highlights", "Highlights")}
                </span>
                {visibleHighlights.length > 0 && (
                  <ul className={styles["mobile-mega-highlight-grid"]}>
                    {visibleHighlights.map((highlight) => (
                      <li key={highlight.id}>
                        <CustomLink
                          className={styles["mobile-mega-highlight-link"]}
                          href={highlight.href}
                          target="_self"
                          onClick={closeMenu}
                          disablePrefetch
                        >
                          <span
                            className={styles["mobile-mega-highlight-title"]}
                            dangerouslySetInnerHTML={{
                              __html: customSanitize(highlight.title),
                            }}
                          />
                          {highlight.subline && (
                            <span
                              className={
                                styles["mobile-mega-highlight-subline"]
                              }
                              dangerouslySetInnerHTML={{
                                __html: customSanitize(highlight.subline),
                              }}
                            />
                          )}
                        </CustomLink>
                      </li>
                    ))}

                    {isLink(megaMenuOption.link_column_end) && (
                      <li>
                        <CustomLink
                          className={styles["mobile-mega-more-link"]}
                          href={megaMenuOption.link_column_end.url}
                          target={megaMenuOption.link_column_end.target}
                          onClick={closeMenu}
                          disablePrefetch
                        >
                          <CustomSVG
                            width={26}
                            height={18}
                            src={getIcon("bare_arrow_right")}
                            wrapper="span"
                            aria-hidden="true"
                          />
                          <SanitizedText
                            text={megaMenuOption.link_column_end.title}
                          />
                        </CustomLink>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {(isLink(megaMenuOption.link_menu_footer) ||
              megaMenuOption.content) && (
              <li className={styles["mobile-mega-footer"]} role="none">
                {isLink(megaMenuOption.link_menu_footer) && (
                  <CustomLink
                    className={styles["mobile-mega-footer-link"]}
                    href={megaMenuOption.link_menu_footer.url}
                    target={megaMenuOption.link_menu_footer.target}
                    onClick={closeMenu}
                    disablePrefetch
                  >
                    <span className={styles["mobile-mega-footer-icon"]}>
                      <CustomSVG
                        width={32}
                        height={32}
                        src={getIcon("yellow_circle_arrow")}
                        wrapper="span"
                        aria-hidden="true"
                        className="is-flex"
                      />
                    </span>
                    <SanitizedText
                      text={megaMenuOption.link_menu_footer.title}
                    />
                  </CustomLink>
                )}
                {megaMenuOption.content && (
                  <div
                    className={styles["mobile-mega-footer-text"]}
                    dangerouslySetInnerHTML={{
                      __html: customSanitize(megaMenuOption.content),
                    }}
                  />
                )}
              </li>
            )}
          </>
        ) : (
          Array.isArray(item.child_items) &&
          item.child_items.map((child, key) => {
            if (!items.current[key]) {
              items.current[key] = React.createRef<HTMLLIElement>();
            }
            return (
              <MenuItem
                item={child}
                key={key}
                pageData={pageData}
                currentSubmenu={currentSubmenu}
                setCurrentSubmenu={setCurrentSubmenu}
                parentSubMenu={item}
                subMenu={item}
                tabIndex={-1}
                itemRef={items.current[key]}
                controlParentMenu={controlMenu}
                onFocus={key == 0 ? firstItemFocused : undefined}
              />
            );
          })
        )}
        <li className="is-hidden-after-navbar-breakpoint" role="none">
          <button
            className={`back button-reset ${styles["back-button"]} is-flex is-align-items-center navbar-sublink`}
            role="menuitem"
            onClick={() => {
              setCurrentSubmenu(parentSubMenu);
            }}
          >
            <CustomSVG
              width={12}
              height={12}
              src={getIcon("altArrowRight")}
              wrapper={"div"}
              aria-hidden="true"
              className={`is-flex ${styles["back-button-icon"]}`}
            />
            <span>{getLabel("back", "Zurück")}</span>
          </button>
        </li>
      </ul>
    </FocusTrap>
  );
};

export default SubMenu;
